import { parseTransactionError } from './parser.js';

/** Retryable RPC/network error message fragments. */
const RETRYABLE_PATTERNS: ReadonlyArray<string> = [
  'BlockhashNotFound',
  'TransactionExpiredBlockheightExceeded',
  'TransactionExpiredTimeoutError',
  'Node is behind',
  'Service Unavailable',
  'Too Many Requests',
  '429',
  '502',
  '503',
  '504',
  'ETIMEDOUT',
  'ECONNRESET',
  'ECONNREFUSED',
  'fetch failed',
];

/**
 * Returns true if the error is a transient/retryable failure (network, blockhash expiry, etc.).
 * Program errors (SlippageExceeded, MarketNotActive, etc.) are NEVER retryable.
 * Uses `parseTransactionError` to detect program errors — if the error contains
 * a recognized program error code, it is always non-retryable.
 */
// ALLOW-UNKNOWN: error parameter must accept unknown types for safe error classification
export function isRetryableError(error: unknown): boolean {
  if (error === null || error === undefined) {
    return false;
  }

  // If error contains a recognized program error code, it is never retryable
  const parsed = parseTransactionError(error);
  if (parsed !== null) {
    return false;
  }

  const message = extractMessage(error);
  return RETRYABLE_PATTERNS.some((pattern) => message.includes(pattern));
}

// ALLOW-UNKNOWN: error parameter must accept unknown types for safe message extraction
function extractMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    // ALLOW-UNKNOWN: narrowing from unknown error object to extract message field
    const msg = (error as { message: unknown }).message;
    if (typeof msg === 'string') {
      return msg;
    }
  }
  return '';
}
